import type { AiSummaryBody } from '@/types'

// The coaching letter arrives as JSON (see the output format in server/prompts.ts). Shared by
// the server's non-streaming route and the app, which parses the streamed letter itself.

/**
 * @param priorityIds the priorities the coach was shown; insights keyed by anything else (a
 *   life area, a rank like "#3000") are dropped so they can't surface on the wrong thing.
 */
export function parseCoachingBody(raw: string, fallbackScore: number | null, priorityIds?: Iterable<string>): AiSummaryBody {
  // Strip DeepSeek-R1 chain-of-thought reasoning blocks.
  // If nothing remains (model output ONLY a <think> block), fall back to searching
  // the full raw text — some R1 variants embed the JSON answer inside the think block.
  const withoutThink = raw.replace(/<think>[\s\S]*?<\/think>/gi, '').trim()
  const candidate = withoutThink || raw

  // Strip markdown code fences models add despite instructions
  const cleaned = candidate
    .replace(/^```(?:json)?\s*/m, '')
    .replace(/\s*```\s*$/m, '')
    .trim()

  if (!cleaned) {
    throw new Error(
      'The model returned an empty response. This can happen when the model only produces reasoning without a final answer. Please try generating again.',
    )
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(cleaned)
  } catch {
    // Last resort: find the outermost JSON object in the text
    const match = cleaned.match(/\{[\s\S]*\}/)
    if (!match) {
      throw new Error(
        `Cannot parse coaching response as JSON.\n\nRaw (truncated):\n${raw.slice(0, 600)}`,
      )
    }
    parsed = JSON.parse(match[0])
  }

  const p = parsed as Record<string, unknown>

  return {
    paragraphs: (p['paragraphs'] as [string, string, string]) ?? ['', '', ''],
    horizons: (p['horizons'] as AiSummaryBody['horizons']) ?? {
      y1: '',
      y3: '',
      y5: '',
      y10: '',
    },
    alignmentScore:
      typeof p['alignmentScore'] === 'number' ? p['alignmentScore'] : fallbackScore,
    flags: (p['flags'] as AiSummaryBody['flags']) ?? [],
    priorityInsights: validInsights(p['priorityInsights'], priorityIds),
  }
}

function validInsights(raw: unknown, priorityIds?: Iterable<string>): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const known = priorityIds ? new Set(priorityIds) : null
  const kept = Object.entries(raw as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim() !== '')
    .filter(([id]) => !known || known.has(id))
  return kept.length > 0 ? Object.fromEntries(kept) : undefined
}

export const CUT_OFF_MESSAGE = 'The letter was cut off before it finished. Try again.'
export const DECLINED_MESSAGE = 'The model declined to write this letter. Try again, or try another coaching style.'

const ESCAPES: Record<string, string> = { '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f', n: '\n', r: '\r', t: '\t' }

/**
 * The letter's paragraphs so far, read from JSON that is still being written: every finished
 * paragraph, plus the one in progress. Returns [] until the "paragraphs" array has started.
 */
export function streamingParagraphs(partial: string): string[] {
  const key = partial.indexOf('"paragraphs"')
  if (key === -1) return []
  const open = partial.indexOf('[', key)
  if (open === -1) return []

  const paragraphs: string[] = []
  let i = open + 1
  while (i < partial.length) {
    const ch = partial[i]!
    if (ch === ']') break
    if (ch !== '"') { i++; continue }

    // A JSON string literal, possibly cut off at the end of what has arrived.
    let text = ''
    i++
    while (i < partial.length) {
      const c = partial[i]!
      if (c === '"') { i++; break }
      if (c !== '\\') { text += c; i++; continue }
      const next = partial[i + 1]
      if (next === undefined) { i = partial.length; break } // escape split across chunks
      if (next === 'u') {
        const hex = partial.slice(i + 2, i + 6)
        if (!/^[0-9a-fA-F]{4}$/.test(hex)) { i = partial.length; break }
        text += String.fromCharCode(parseInt(hex, 16))
        i += 6
        continue
      }
      text += ESCAPES[next] ?? next
      i += 2
    }
    paragraphs.push(text)
  }
  return paragraphs
}
